import { deepMerge } from '@/helpers/deepMerge';
import { generateId } from '@/helpers/generateId';
import { timeout } from '@/helpers/timeout';
import { type TSocketBatchAction, disciplineIds, socketBatchActions } from '@/config/constants';

import { AbstractEmitter } from './AbstractEmitter';
import { logger } from './logger';
import {
  AllEmitterEvents,
  EmitterEvents,
  SocketEmitter,
  type SocketServiceOptions,
} from './SocketEmitter';
import {
  type ISportsbookCascadeManagerParams,
  SportsbookSocketCascadeManager,
} from './SportsbookSocketCascadeManager';

export const SportsbookManagerEvents = {
  ...EmitterEvents,
  COUNT: 'count',
  DISCIPLINE: 'discipline',
  EVENT: 'event',
  LINE: 'line',
  MARKET: 'market',
  RECONNECT: 'reconnect',
  RECONNECT_SUCCESS: 'reconnect_success',
  SCOREBOARD: 'scoreboard',
  TOP_TOURNAMENT: 'topTournament',
  TOURNAMENT: 'tournament',
  UNABLE_RECONNECT: 'unable_reconnect',
  WIDGET: 'widget',
} as const;

export class SportsbookSocketManager extends AbstractEmitter<
  TSportsbookManagerEvents,
  SportsbookEventPayloads[TSportsbookManagerEvents]
> {
  private static instance: SportsbookSocketManager | null = null;
  socketEmitter: SocketEmitter | null = null;

  config: Config | null = null;

  subscribersMap: Map<string, (data: any) => void> = new Map();

  fibersMap: Map<string, SubscribeFiber> = new Map();
  initParamsGetter: () => Promise<SocketServiceOptions | null> = async () => {
    console.error('initParamsGetter not initialized');
    return null;
  };

  reconnectLimit: number = 10;
  reconnectCount: number = 0;

  static getInstance(): SportsbookSocketManager {
    if (!SportsbookSocketManager.instance) {
      SportsbookSocketManager.instance = new SportsbookSocketManager();
    }

    return SportsbookSocketManager.instance;
  }

  private constructor() {
    super();
  }

  async initialize({ reconnectLimit = 10, initParamsGetter }: InitParams) {
    this.socketEmitter = SocketEmitter.getInstance();

    this.initListeners();
    this.initParamsGetter = initParamsGetter;

    this.reconnectLimit = reconnectLimit;

    const initParams = await this.initParamsGetter();

    if (!initParams) {
      return;
    }

    await this.socketEmitter.connect(initParams);
  }

  initListeners() {
    if (!this.socketEmitter) return;

    this.socketEmitter.on(AllEmitterEvents.CONFIG, this.onConfigChange);

    this.socketEmitter.on(AllEmitterEvents.LEAVE, this.onLeave);

    this.socketEmitter.on(AllEmitterEvents.BATCH, this.onBatchUpdate);

    this.socketEmitter.on(AllEmitterEvents.ERROR, this.onError);

    this.socketEmitter.on(AllEmitterEvents.UNKNOWN_EVENT, this.onUnknownEvent);

    this.socketEmitter.on(AllEmitterEvents.SOCKET_CLOSED, this.onSocketClosed);

    this.socketEmitter.on(AllEmitterEvents.SOCKET_CONNECTIVITY_ERROR, this.onConnectivityError);

    this.socketEmitter.on(AllEmitterEvents.SEND_ERROR, this.onSendError);

    this.socketEmitter.on(AllEmitterEvents.SOCKET_AUTH_ERROR, this.socketAuthError);
  }

  socketAuthError = (data: any) => {
    this.emit(SportsbookManagerEvents.SOCKET_AUTH_ERROR, data);
  };

  onConnectivityError = () => {
    this.reconnect();
  };

  onUnknownEvent = (data: any) => {
    this.emit(SportsbookManagerEvents.UNKNOWN_EVENT, data);
  };

  onSendError = (data: any) => {
    this.emit(SportsbookManagerEvents.SEND_ERROR, data);
  };

  onSocketClosed = async ({ payload: { wasClean, code, reason } }: any) => {
    this.emit(SportsbookManagerEvents.SOCKET_CLOSED, { code, reason, wasClean });
  };

  async reconnect(type: 'manual' | 'automatic' = 'automatic') {
    if (type === 'automatic' && this.reconnectCount >= this.reconnectLimit) {
      this.emit(SportsbookManagerEvents.UNABLE_RECONNECT, { count: this.reconnectCount });
      return;
    }

    if (type === 'automatic') {
      this.reconnectCount++;

      let delay: number = 0;

      if (this.reconnectCount === 1) {
        delay = 0;
      } else if (this.reconnectCount < 5) {
        delay = 1;
      }

      if (this.reconnectCount >= 5) {
        delay = 10;
      }

      this.emit(SportsbookManagerEvents.RECONNECT, {
        count: this.reconnectCount,
        nextAttemptIn: delay,
      });

      await timeout(delay);
    }

    const initParams = await this.initParamsGetter();

    if (!initParams) {
      logger.error('Failed to get init params for socket connection');
      return;
    }

    await this.socketEmitter?.connect(initParams);
    this.resubscribe();

    this.emit(SportsbookManagerEvents.RECONNECT_SUCCESS, undefined);

    this.reconnectCount = 0;
  }

  resubscribe() {
    if (!this.socketEmitter?.getIsWSReady()) {
      return;
    }

    const entitiesToSubscribeMap: Map<TFiberType, Set<string>> = new Map();

    for (const fiber of this.fibersMap.values()) {
      if (!entitiesToSubscribeMap.has(fiber.type)) {
        entitiesToSubscribeMap.set(fiber.type, new Set());
      }

      entitiesToSubscribeMap.get(fiber.type)?.add(fiber.id);
    }

    for (const [type, idsSet] of entitiesToSubscribeMap.entries()) {
      if (idsSet.size === 0) {
        continue;
      }
      this.socketEmitter.send('join', type, Array.from(idsSet));
    }
  }

  onError = (data: any) => {
    const {
      code,
      message,
      data: { id, model },
    } = data.payload;

    const fiber = this.fibersMap.get(getFiberKey(model, id));

    if (!fiber) {
      logger.error(`Received error for non-subscribed fiber ${model} with id ${id}`, data);
      return;
    }

    const subscriberIds = Array.from(fiber.subscribersIds);

    if (code === 106) {
      this.fibersMap.delete(getFiberKey(model, id));
    }

    for (const subscriberId of subscriberIds) {
      this.emitTo(subscriberId, model, {
        data: { action: null, model, value: { code, id, message } },
        type: 'error',
      });
    }
  };

  createCascadeSubscription = ({
    rootModel,
    rootModelIds,
    targetModel,
    key,
  }: Omit<ISportsbookCascadeManagerParams, 'socketInstance'>): SportsbookSocketCascadeManager => {
    return new SportsbookSocketCascadeManager({
      key,
      rootModel,
      rootModelIds,
      socketInstance: this,
      targetModel,
    });
  };

  onBatchUpdate = (data: { payload: TSocketBatchUpdate[] }) => {
    const { payload } = data;

    for (const fiberUpdate of payload) {
      const [model, id, action, value] = fiberUpdate;

      if (model === 'count' && id === 'count') {
        this.handleCountUpdate(value);
        continue;
      }

      const fiberKey = getFiberKey(model, id);

      const fiber = this.fibersMap.get(fiberKey);

      // Model 'line' is exception because it has raw id by default as root entity
      const isOneOfRawId =
        model !== 'line' && this.fibersMap.get(getFiberKey(model, (value as any).id));

      if (!fiber || isOneOfRawId) {
        this.handleUnknownFiber(model, value);

        continue;
      }

      /**
       * Backend can send partial updates, so we need to merge them
       * For market we need to merge updates recursively because they can have nested structure
       * and backend can send partial updates for nested entities as well
       */
      const updatedValue = deepMerge(fiber.body, value);
      const updatedFiber: SubscribeFiber = { ...fiber, body: updatedValue };
      this.fibersMap.set(fiberKey, updatedFiber);

      fiber.subscribersIds.forEach((subscriberId) => {
        this.emitTo(subscriberId, model, {
          data: { action, model, value: updatedValue },
          type: 'batch',
        });
      });
    }
  };

  /*
   * Backend can send updates for fibers that were not subscribed (e.g., due to raw IDs).
   * In this case, we need to mark the fiber as having a raw ID to avoid unsubscribing with it later.
   * And we need to resend the subscription to all subscribers of this fiber.
   */
  handleUnknownFiber = (model: TFiberType, value: any) => {
    const targetkey = getFiberKey(model, value.id);

    const targetFiber = this.fibersMap.get(targetkey);

    if (!targetFiber) {
      return;
    }

    targetFiber.isRawId = true;
    targetFiber.childrenIds.add(value.lid);
    this.fibersMap.set(targetkey, targetFiber);

    const subscriberIds = [...targetFiber.subscribersIds];

    const existedFiberKey = getFiberKey(model, value.lid);
    const existedFiber = this.fibersMap.get(existedFiberKey);

    /**
     * There is a chance that someone already subscribed to this fiber with full id
     * so we need to merge subscribers and resend subscription to avoid missing updates
     */
    if (existedFiber) {
      existedFiber.subscribersIds = new Set([...existedFiber.subscribersIds, ...subscriberIds]);

      existedFiber.subscribersIds.forEach((subscriberId) => {
        this.emitTo(subscriberId, model, {
          data: { action: socketBatchActions.UPDATE, model, value },
          type: 'batch',
        });
      });
      /**
       * If there is no existed fiber it means that we received update for non-subscribed fiber with raw id,
       * so we just create new fiber with full id and emit update to subscribers without resending join to backend,
       * because we already have newest value
       */
    } else {
      const newFiber: SubscribeFiber = {
        body: value,
        childrenIds: new Set(),
        id: value.lid,
        isRawId: false,
        subscribersIds: new Set(subscriberIds),
        subscribersRequestedLeave: new Set(),
        type: model,
      };

      this.fibersMap.set(existedFiberKey, newFiber);

      subscriberIds.forEach((subscriberId) => {
        this.emitTo(subscriberId, model, {
          data: { action: socketBatchActions.CREATE, model, value },
          type: 'batch',
        });
      });
    }
  };

  onLeave = ({ data, payload }: { data: string[]; payload: TFiberType }) => {
    const subscriberIds = new Set<string>();
    const idsToLeave = new Set(data);
    const fibersToDelete: SubscribeFiber[] = [];

    for (const id of data) {
      const fiberKey = getFiberKey(payload, id);
      const fiber = this.fibersMap.get(fiberKey);

      if (fiber) {
        // There are subscribers who send leave but we have new subscribers
        if (fiber.subscribersRequestedLeave.size > 0 && fiber.subscribersIds.size > 0) {
          fiber.subscribersRequestedLeave?.clear();

          this.fibersMap.set(fiberKey, fiber);

          this.socketEmitter?.send('join', fiber.type, [id]);

          // We dont send leave for subscribers beacuse there are new subscribers
          idsToLeave.delete(id);
          continue;
        }

        fiber.subscribersIds.forEach((subscriberId) => {
          subscriberIds.add(subscriberId);
        });

        this.fibersMap.delete(fiberKey);
        fibersToDelete.push(fiber);
      }
    }

    const idsToLeaveArr = Array.from(idsToLeave);

    if (subscriberIds.size === 0 && idsToLeave.size > 0) {
      /**
       * Emit to root Socket event if there are no subscribers to notify to delete entities
       * Need to remove after Root Socket refactoring
       */
      this.emitTo('', payload, {
        data: { action: socketBatchActions.DELETE, model: payload, value: idsToLeaveArr },
        type: 'leave',
      });
    }

    // Uncomment for debugging purposes
    // console.log({ fibers: this.fibersMap });

    fibersToDelete.forEach(({ subscribersIds, id }) => {
      for (const subscriberId of subscribersIds) {
        this.emitTo(subscriberId, payload, {
          data: { action: socketBatchActions.DELETE, model: payload, value: id },
          type: 'leave',
        });
      }
    });
  };

  handleCountUpdate = (value: any) => {
    for (const key of Object.keys(value)) {
      const fiber = this.fibersMap.get(getFiberKey('count', key));

      if (fiber) {
        fiber.body = { ...fiber.body, ...value[key] };
        this.fibersMap.set(getFiberKey('count', key), fiber);

        this.emit(SportsbookManagerEvents.COUNT, {
          data: { action: null, model: 'count', value: { ...fiber.body, key } },
          type: 'batch',
        });
      }
    }
  };

  onConfigChange = (config: { payload: Config; model: string }) => {
    this.config = config.payload;

    this.subscribersMap.forEach((onChange) => {
      onChange(this.config);
    });
  };

  registerSubscriber({
    onConfigChange,
    key,
  }: {
    onConfigChange?: (data: any) => void;
    key?: string | undefined;
  }) {
    const id = key ? `${key}_${generateId()}` : generateId();
    this.subscribersMap.set(id, onConfigChange || (() => {}));

    const unregisterFn = () => {
      this.unregisterSubscriber(id);
    };

    const { on, off, subscribe } = this.scope(id);

    return {
      config: this.config,
      off,
      on,
      subscribe,
      subscribeId: id,
      unregister: unregisterFn,
    };
  }

  scope(subscriberId: string) {
    const { on, off } = this.emitterScope(subscriberId);

    const subscribe = (type: TFiberType, ids: string[]) => {
      this.subscribe(subscriberId, type, ids);
    };

    return {
      off,
      on,
      subscribe,
    };
  }

  unregisterSubscriber(subscriberId: string) {
    this.offRecipient(subscriberId);
    this.subscribersMap.delete(subscriberId);

    const idsByType: Map<TFiberType, Set<string>> = new Map();

    for (const [fiberKey, fiber] of this.fibersMap.entries()) {
      if (fiber.subscribersIds.has(subscriberId)) {
        fiber.subscribersIds.delete(subscriberId);

        if (fiber.subscribersIds.size === 0) {
          fiber.subscribersRequestedLeave.add(subscriberId);

          if (!idsByType.has(fiber.type)) {
            idsByType.set(fiber.type, new Set());
          }

          idsByType.get(fiber.type)?.add(fiber.id);
        }

        this.fibersMap.set(fiberKey, fiber);
      }
    }

    if (idsByType.size === 0) {
      return;
    }

    for (const type of Array.from(idsByType.keys()) as TFiberType[]) {
      this.socketEmitter?.send('leave', type, Array.from(idsByType.get(type) || []));
    }

    // Uncomment for debugging purposes
    // console.log({ fibers: this.fibersMap });
  }

  subscribe(subscriberId: string, type: TFiberType, ids: string[]) {
    const newFibersIds: string[] = [];

    for (const id of ids) {
      const fiber = this.fibersMap.get(getFiberKey(type, id));

      if (fiber) {
        fiber.subscribersIds.add(subscriberId);
        this.fibersMap.set(getFiberKey(type, id), fiber);

        if (fiber.isRawId) {
          for (const childId of fiber.childrenIds) {
            const childFiber = this.fibersMap.get(getFiberKey(type, childId));

            if (childFiber) {
              childFiber.subscribersIds.add(subscriberId);
              this.fibersMap.set(getFiberKey(type, childId), childFiber);

              this.emitTo(subscriberId, type, {
                data: { action: null, model: type, value: childFiber.body },
                type: 'batch',
              });
            }
          }
        }

        if (fiber.body) {
          this.emitTo(subscriberId, type, {
            /**
             * Action is null because this is not a new subscription, just sending existing data
             * To avoid root Socket unnecessary processing
             */
            data: { action: null, model: type, value: fiber.body },
            type: 'batch',
          });
        }
      } else {
        const newFiber: SubscribeFiber = {
          body: null,
          childrenIds: new Set(),
          id,
          isRawId: false,
          subscribersIds: new Set([subscriberId]),
          subscribersRequestedLeave: new Set(),
          type,
        };
        this.fibersMap.set(getFiberKey(type, id), newFiber);
        newFibersIds.push(id);
      }
    }

    if (newFibersIds.length > 0) {
      this.socketEmitter?.send('join', type, newFibersIds);

      // Uncomment for debugging purposes
      // console.log({ fibers: this.fibersMap });
    }
  }

  unsubscribe(subscriberId: string, model: TFiberType, ids: string[]) {
    const idsToUnsubscribe: string[] = [];

    for (const id of ids) {
      const fiber = this.fibersMap.get(getFiberKey(model, id));

      if (fiber) {
        fiber.subscribersIds.delete(subscriberId);
        fiber.subscribersRequestedLeave.add(subscriberId);

        if (fiber.subscribersIds.size === 0) {
          if (!fiber.isRawId) {
            idsToUnsubscribe.push(id);
          }

          fiber.childrenIds.forEach((childId) => {
            const childFiber = this.fibersMap.get(getFiberKey(model, childId));

            if (childFiber) {
              childFiber.subscribersIds.delete(subscriberId);
              this.fibersMap.set(getFiberKey(model, childId), childFiber);

              if (childFiber.subscribersIds.size === 0) {
                idsToUnsubscribe.push(childId);
              }
            }
          });
        }
      } else {
        logger.warn(`Trying to unsubscribe from ${model} with id ${id} non-subscribed fiber`);
      }
    }

    if (idsToUnsubscribe.length > 0) {
      this.socketEmitter?.send('leave', model, idsToUnsubscribe);

      // Uncomment for debugging purposes
      // console.log({ fibers: this.fibersMap });
    }
  }

  disconnectSocket() {
    this.socketEmitter?.closeConnection();
  }

  async connectSocket() {
    await this.reconnect('manual');
  }

  async getRPC(type: URpcType, rpcRequestData: { reqId: number; [key: string]: any }) {
    return new Promise((resolve) => {
      const onRPCResponse = (data: any) => {
        const {
          data: { reqId, ...restData },
        } = data;

        if (reqId && reqId !== rpcRequestData.reqId) {
          return;
        }

        resolve(restData);
        this.socketEmitter?.off(AllEmitterEvents.RPC, onRPCResponse);
      };

      this.socketEmitter?.on(AllEmitterEvents.RPC, onRPCResponse);

      this.socketEmitter?.send(AllEmitterEvents.RPC, type, rpcRequestData);
    });
  }

  /**
   * Only for testing purposes
   */
  static _destroy() {
    if (SportsbookSocketManager.instance) {
      SportsbookSocketManager.instance.cleanup();
    }

    SportsbookSocketManager.instance = null;
  }

  cleanup() {
    this.disconnectSocket();
    this.subscribersMap.clear();
    this.fibersMap.clear();

    if (this.socketEmitter) {
      SocketEmitter._destroy();
      this.socketEmitter = null;
    }
  }
}

const getFiberKey = (type: TFiberType, id: string) => {
  return `${type}__${id}`;
};

type TRpcTypes = ['search', 'topTournaments', 'disciplineSummary'];
export type URpcType = TRpcTypes[number];

type TSportsbookManagerEvents =
  (typeof SportsbookManagerEvents)[keyof typeof SportsbookManagerEvents];

interface InitParams {
  reconnectLimit?: number;
  initParamsGetter: () => Promise<SocketServiceOptions | null>;
}

export interface Config {
  cid: string;
  format: string;
  lang: string;
  disciplines: Array<{
    id: TDisciplineId;
    children: any[];
  }>;
}

interface SubscribeFiber {
  type: TFiberType;
  id: string;
  // TODO: provide proper type
  body: any;
  subscribersIds: Set<string>;
  /**
   * Event Node can process raw ids.
   * SportsbookSocketManager should know whether the id is raw or processed
   * to avoid unsubscribing with raw id
   */
  isRawId: boolean;
  /**
   * Set of children ids for fibers with raw ids
   * For silent subscribe when a few subscribers want the same raw id fiber
   */
  childrenIds: Set<string>;
  subscribersRequestedLeave: Set<string>;
}

type TDisciplineId = (typeof disciplineIds)[number];

export type TFiberType =
  | 'count'
  | 'line'
  | 'discipline'
  | 'tournament'
  | 'widget'
  | 'topTournament'
  | 'event'
  | 'market'
  | 'scoreboard';

export type TSocketBatchPayload<T = unknown> = {
  type: 'batch';
  data: {
    model: TFiberType;
    action: TSocketBatchAction | null;
    value: T;
  };
};

export type TSocketLeavePayload = {
  type: 'leave';
  data: {
    model: TFiberType;
    action: null;
    value: string[];
  };
};

export type TSocketErrorPayload = {
  type: 'error';
  data: {
    model: TFiberType;
    action: null;
    value: {
      code: number;
      id: string;
      message: string;
    };
  };
};

export type TSportsbookSocketPayload<T = unknown> =
  | TSocketBatchPayload<T>
  | TSocketLeavePayload
  | TSocketErrorPayload;

export type TSocketBatchUpdate<T = any> = [
  model: TFiberType,
  id: string,
  action: TSocketBatchAction,
  value: T[],
];

type NoPayload = undefined;

export type SportsbookEventPayloads = {
  count: TSportsbookSocketPayload;
  discipline: TSportsbookSocketPayload;
  event: TSportsbookSocketPayload;
  line: TSportsbookSocketPayload;
  market: TSportsbookSocketPayload;
  scoreboard: TSportsbookSocketPayload;
  tournament: TSportsbookSocketPayload;
  topTournament: TSportsbookSocketPayload;
  widget: TSportsbookSocketPayload;

  reconnect: {
    count: number;
    nextAttemptIn: number;
  };
  reconnect_success: NoPayload;
  unable_reconnect: {
    count: number;
  };
  send_error: any;
  socket_auth_error: any;
  socket_closed: {
    wasClean: boolean;
    code: number;
    reason: string;
  };
  unknown_event: any;
  socket_connectivity_error: NoPayload;
};
